import { useEffect, useState } from 'react';
import { Settings2, Save, RefreshCw, DollarSign, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { api } from '../../context/AuthContext';
import { useAuth } from '../../context/AuthContext';

const TIER_ORDER = ['basic', 'silver', 'gold', 'platinum'];

const GATE_CATEGORIES = {
  Features: [
    'keyword_tracking', 'on_page_seo', 'competitor_analysis', 'structured_data',
    'press_releases', 'backlink_analysis', 'hyperlink_optimization',
    'permalink_analysis', 'internal_linking', 'infographics', 'geotagging',
  ],
  Integrations: [
    'google_analytics', 'gsc', 'google_business_page', 'google_indexing',
    'pagespeed', 'bing_webmaster', 'bing_places',
    'facebook', 'linkedin', 'twitter', 'pinterest', 'youtube',
    'cms_wordpress', 'cms_shopify', 'cms_wix', 'cms_squarespace',
  ],
};

const ALL_GATE_VALUES = ['off', ...TIER_ORDER];
const DEFAULT_PRICES = { basic: 500, silver: 800, gold: 1200, platinum: 1800 };

export default function AdminPackagesPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [tiers, setTiers] = useState([]);
  const [gates, setGates] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState('');
  const [usageRows, setUsageRows] = useState([]);
  const [usageLoading, setUsageLoading] = useState(false);
  const [resetting, setResetting] = useState('');

  async function load() {
    setLoading(true);
    try {
      const r = await api.get('/admin/packages');
      const fetched = r.data?.tiers || [];
      const ordered = [...fetched]
        .sort((a, b) => TIER_ORDER.indexOf(a.tier) - TIER_ORDER.indexOf(b.tier))
        .map((t) => ({ ...t, price: t.price ?? DEFAULT_PRICES[t.tier] ?? 0 }));
      setTiers(ordered);
      setGates(r.data?.gates || {});
    } catch (e) {
      toast.error('Failed to load package configs');
    } finally {
      setLoading(false);
    }
  }

  async function loadUsage() {
    setUsageLoading(true);
    try {
      const r = await api.get('/admin/packages/usage');
      setUsageRows(r.data?.rows || []);
    } catch (e) {
      toast.error('Failed to load project usage');
    } finally {
      setUsageLoading(false);
    }
  }

  useEffect(() => {
    if (isAdmin) { load(); loadUsage(); }
  }, [isAdmin]);

  async function resetUsage(row) {
    const reason = window.prompt(
      `Reset this month's usage for "${row.name || row.project_id}"?\n\n` +
      `This credits usage back to 0 (raw consumption is kept for audit). ` +
      `Enter a reason:`,
    );
    if (reason === null) return;            // cancelled
    if (!reason.trim()) { toast.error('A reason is required.'); return; }
    setResetting(row.project_id);
    try {
      await api.post(`/admin/packages/usage/${row.project_id}/reset`, {
        reason: reason.trim(),
        idempotency_key: `${row.project_id}-${Date.now()}`,
      });
      toast.success('Usage reset for this project.');
      await loadUsage();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reset failed');
    } finally {
      setResetting('');
    }
  }

  if (!isAdmin) {
    return (
      <div className="p-8 text-center text-slate-500">
        Admin access required.
      </div>
    );
  }

  function updateLimit(tierName, metric, value) {
    setTiers((current) =>
      current.map((t) =>
        t.tier === tierName
          ? { ...t, limits: { ...t.limits, [metric]: Number(value) || 0 } }
          : t,
      ),
    );
  }

  function updatePrice(tierName, value) {
    setTiers((current) =>
      current.map((t) =>
        t.tier === tierName ? { ...t, price: Number(value) || 0 } : t,
      ),
    );
  }

  async function saveTier(tier) {
    setSaving(tier.tier);
    try {
      await api.patch(`/admin/packages/${tier.tier}`, {
        limits: tier.limits,
        price: tier.price,
      });
      toast.success(`Saved ${tier.tier} limits & price`);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || 'Save failed');
    } finally {
      setSaving('');
    }
  }

  async function saveGates() {
    setSaving('__gates__');
    try {
      await api.patch('/admin/packages/__gates__', { gates });
      toast.success('Saved feature gates');
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || 'Save failed');
    } finally {
      setSaving('');
    }
  }

  function updateGate(feature, tierValue) {
    setGates((g) => ({ ...g, [feature]: tierValue }));
  }

  // Discover the union of metric keys across tiers so we can render a stable column order
  const allMetrics = Array.from(
    new Set(tiers.flatMap((t) => Object.keys(t.limits || {}))),
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Settings2 className="h-6 w-6 text-blue-500 dark:text-blue-400" />
            Package Configuration
          </h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">
            Edit per-tier monthly limits and feature gates. Changes apply within 60 seconds.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {tiers.map((tier) => (
        <Card key={tier.tier}>
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold capitalize text-slate-900 dark:text-white">
                {tier.tier}
              </h2>
              <Button
                size="sm"
                onClick={() => saveTier(tier)}
                disabled={saving === tier.tier}
              >
                <Save className="h-4 w-4 mr-2" />
                {saving === tier.tier ? 'Saving…' : 'Save'}
              </Button>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 w-fit">
              <DollarSign className="h-4 w-4 text-green-500 flex-shrink-0" />
              <label className="text-xs text-slate-600 dark:text-slate-300 flex items-center gap-2">
                <span className="font-medium">Monthly Price (USD)</span>
                <Input
                  type="number"
                  min={0}
                  step={0.01}
                  className="w-32"
                  value={tier.price ?? 0}
                  onChange={(e) => updatePrice(tier.tier, e.target.value)}
                />
              </label>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {allMetrics.map((metric) => (
                <label key={metric} className="text-xs text-slate-600 dark:text-slate-300 flex flex-col gap-1">
                  <span className="capitalize">{metric.replace(/_/g, ' ')}</span>
                  <Input
                    type="number"
                    min={0}
                    value={tier.limits?.[metric] ?? 0}
                    onChange={(e) => updateLimit(tier.tier, metric, e.target.value)}
                  />
                </label>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}

      {(() => {
        const allFeatures = Object.keys(gates);
        const categorized = new Set([
          ...GATE_CATEGORIES.Features,
          ...GATE_CATEGORIES.Integrations,
        ]);
        const other = allFeatures.filter((f) => !categorized.has(f));
        const sections = [
          { label: 'Feature Gates', keys: GATE_CATEGORIES.Features.filter((f) => f in gates) },
          { label: 'Integration Gates', keys: GATE_CATEGORIES.Integrations.filter((f) => f in gates) },
          ...(other.length > 0 ? [{ label: 'Other', keys: other }] : []),
        ].filter((s) => s.keys.length > 0);

        return sections.map((section) => (
          <Card key={section.label}>
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                  {section.label}
                </h2>
                <Button size="sm" onClick={saveGates} disabled={saving === '__gates__'}>
                  <Save className="h-4 w-4 mr-2" />
                  {saving === '__gates__' ? 'Saving…' : 'Save'}
                </Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {section.keys.map((feature) => (
                  <label key={feature} className="text-xs text-slate-600 dark:text-slate-300 flex flex-col gap-1">
                    <span className="capitalize">{feature.replace(/_/g, ' ')}</span>
                    <select
                      value={gates[feature] ?? 'basic'}
                      onChange={(e) => updateGate(feature, e.target.value)}
                      className="h-9 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 text-sm text-slate-900 dark:text-slate-100 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="off">Disabled</option>
                      {TIER_ORDER.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </label>
                ))}
              </div>
            </CardContent>
          </Card>
        ));
      })()}

      <Card>
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                Project Usage &amp; Resets
              </h2>
              <p className="text-slate-500 dark:text-slate-400 text-xs mt-1">
                This month's usage per project. Reset credits usage back to 0 without erasing the raw consumption record.
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={loadUsage} disabled={usageLoading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${usageLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {usageRows.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {usageLoading ? 'Loading…' : 'No projects with usage this month.'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                    <th className="py-2 pr-4 font-medium">Project</th>
                    <th className="py-2 pr-4 font-medium">Plan</th>
                    <th className="py-2 pr-4 font-medium">Usage (used / limit)</th>
                    <th className="py-2 pr-4 font-medium text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {usageRows.map((row) => {
                    const limits = row.limits || {};
                    const counters = row.counters || {};
                    const adjustments = row.adjustments || {};
                    const metricKeys = Object.keys(limits).filter((m) => limits[m] !== 0);
                    return (
                      <tr key={row.project_id} className="border-b border-slate-100 dark:border-slate-800 align-top">
                        <td className="py-2 pr-4 text-slate-900 dark:text-white">{row.name || row.project_id}</td>
                        <td className="py-2 pr-4 capitalize text-slate-600 dark:text-slate-300">{row.package_type || '—'}</td>
                        <td className="py-2 pr-4">
                          <div className="flex flex-wrap gap-1.5">
                            {metricKeys.length === 0 && <span className="text-slate-400 text-xs">No usage</span>}
                            {metricKeys.map((m) => {
                              const limit = limits[m];
                              const used = Math.max(0, (counters[m] || 0) - (adjustments[m] || 0));
                              const credited = adjustments[m] || 0;
                              const over = limit > 0 && used >= limit;
                              return (
                                <span
                                  key={m}
                                  title={credited ? `raw ${counters[m] || 0} − credit ${credited}` : undefined}
                                  className={`px-2 py-0.5 rounded text-xs ${over ? 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300' : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'}`}
                                >
                                  {m.replace(/_/g, ' ')}: {used}/{limit === -1 ? '∞' : limit}
                                  {credited > 0 && <span className="ml-1 text-emerald-600 dark:text-emerald-400">(−{credited})</span>}
                                </span>
                              );
                            })}
                          </div>
                        </td>
                        <td className="py-2 pr-0 text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => resetUsage(row)}
                            disabled={resetting === row.project_id}
                          >
                            <RotateCcw className={`h-4 w-4 mr-2 ${resetting === row.project_id ? 'animate-spin' : ''}`} />
                            {resetting === row.project_id ? 'Resetting…' : 'Reset'}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
